/**
 * DashProducts — Phase 2B
 * ─────────────────────────────────────────────────────────────────
 * Universal, category-adaptive catalog panel. Business owners publish
 * their offerings (finance products / insurance / services / BNPL plans
 * / parts / payment solutions / offers) with dynamic per-kind fields.
 *
 * Each item can be toggled active/inactive AND public/private so the
 * business controls what appears on its public profile page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, RefreshCw, Package, Edit3, Trash2, X, Eye, EyeOff, Check, ToggleLeft, ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";

/* Which product kinds are relevant for each category. Extensible without a schema migration. */
const KINDS_BY_CATEGORY = {
  bank:                ["finance_product", "offer"],
  finance_company:     ["finance_product", "offer"],
  financing_platform:  ["finance_product", "offer"],
  fintech:             ["finance_product", "payment_solution", "offer"],
  bnpl_service:        ["bnpl_plan", "offer"],
  leasing_company:     ["leasing_product", "offer"],
  insurance_company:   ["insurance_product", "offer"],
  insurance_broker:    ["insurance_product", "offer"],
  payment_provider:    ["payment_solution", "offer"],
  workshop:            ["service", "part", "offer"],
  car_wash:            ["service", "offer"],
  inspection_center:   ["service", "offer"],
  periodic_inspection: ["service", "offer"],
  recovery_towing:     ["service", "offer"],
  tire_battery_shop:   ["service", "part", "offer"],
  parts_store:         ["part", "offer"],
  salvage:             ["part", "offer"],
  transport_company:   ["service", "offer"],
  dealer:              ["offer", "service"],
  official_agency:     ["offer", "service"],
  auction_house:       ["offer"],
  rental_company:      ["offer", "service"],
  individual:          ["offer"],
  other:               ["service", "offer", "other"],
};

const KIND_LABELS = {
  finance_product:   { ar: "منتج تمويلي",         en: "Finance Product" },
  bnpl_plan:         { ar: "خطة تقسيط",           en: "BNPL Plan" },
  leasing_product:   { ar: "منتج تأجير تمويلي",   en: "Leasing Product" },
  insurance_product: { ar: "منتج تأمين",          en: "Insurance Product" },
  service:           { ar: "خدمة",                en: "Service" },
  part:              { ar: "قطعة غيار",           en: "Part" },
  payment_solution:  { ar: "حل دفع",              en: "Payment Solution" },
  offer:             { ar: "عرض ترويجي",          en: "Promotion" },
  other:             { ar: "أخرى",                en: "Other" },
};

/* Per-kind dynamic fields — free-form; sent inside `spec`. */
const KIND_FIELDS = {
  finance_product: [
    { key: "min_amount",         type: "number", ar: "أقل مبلغ (ر.س)", en: "Min amount (SAR)" },
    { key: "max_amount",         type: "number", ar: "أعلى مبلغ (ر.س)", en: "Max amount (SAR)" },
    { key: "tenure_months_max",  type: "number", ar: "أقصى مدة (شهر)",  en: "Max tenure (months)" },
    { key: "profit_rate_percent", type: "number", ar: "هامش الربح %",   en: "Profit rate %" },
    { key: "requirements",       type: "text",   ar: "المتطلبات",       en: "Requirements" },
  ],
  bnpl_plan: [
    { key: "min_amount",   type: "number", ar: "أقل مبلغ (ر.س)",   en: "Min amount (SAR)" },
    { key: "max_amount",   type: "number", ar: "أعلى مبلغ (ر.س)",  en: "Max amount (SAR)" },
    { key: "installments", type: "number", ar: "عدد الأقساط",       en: "Number of installments" },
    { key: "fee_percent",  type: "number", ar: "الرسوم %",          en: "Fees %" },
    { key: "requirements", type: "text",   ar: "المتطلبات",         en: "Requirements" },
  ],
  leasing_product: [
    { key: "asset_type",         type: "text",   ar: "نوع الأصل",       en: "Asset type" },
    { key: "min_amount",         type: "number", ar: "أقل مبلغ (ر.س)",  en: "Min amount (SAR)" },
    { key: "max_amount",         type: "number", ar: "أعلى مبلغ (ر.س)", en: "Max amount (SAR)" },
    { key: "tenure_months_max",  type: "number", ar: "أقصى مدة (شهر)",  en: "Max tenure (months)" },
    { key: "down_payment_percent", type: "number", ar: "الدفعة الأولى %", en: "Down payment %" },
  ],
  insurance_product: [
    { key: "coverage_type",  type: "text",   ar: "نوع التغطية",     en: "Coverage type" },
    { key: "min_premium",    type: "number", ar: "أقل قسط سنوي",   en: "Min annual premium" },
    { key: "coverage_limit", type: "number", ar: "حد التغطية",     en: "Coverage limit" },
    { key: "requirements",   type: "text",   ar: "المتطلبات",       en: "Requirements" },
  ],
  service: [
    { key: "duration_minutes", type: "number", ar: "المدة التقديرية (دقيقة)", en: "Duration (minutes)" },
    { key: "location",         type: "text",   ar: "الموقع/المدينة",           en: "Location / City" },
  ],
  part: [
    { key: "part_number", type: "text",   ar: "رقم القطعة",  en: "Part number" },
    { key: "brand",       type: "text",   ar: "العلامة",     en: "Brand" },
    { key: "stock",       type: "number", ar: "الكمية",      en: "Stock" },
    { key: "warranty",    type: "text",   ar: "الضمان",      en: "Warranty" },
  ],
  payment_solution: [
    { key: "fee_percent",   type: "number", ar: "الرسوم %",      en: "Fee %" },
    { key: "supported",     type: "text",   ar: "طرق مدعومة",    en: "Supported methods" },
    { key: "settlement_days", type: "number", ar: "أيام التسوية", en: "Settlement days" },
  ],
  offer: [
    { key: "discount_percent", type: "number", ar: "نسبة الخصم %", en: "Discount %" },
    { key: "valid_until",      type: "text",   ar: "ساري حتى",     en: "Valid until" },
    { key: "terms",            type: "text",   ar: "الشروط",       en: "Terms" },
  ],
  other: [
    { key: "notes", type: "text", ar: "ملاحظات", en: "Notes" },
  ],
};


export default function DashProducts({ businessId, home }) {
  const { lang } = useI18n();
  const category = home?.business?.category || "other";
  const allowedKinds = KINDS_BY_CATEGORY[category] || ["other"];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product being edited, or "new"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/platform/businesses/${businessId}/products`);
      setItems(data.items || []);
    } catch {
      toast.error(lang === "ar" ? "تعذّر التحميل" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [businessId, lang]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (p, field) => {
    try {
      await apiClient.patch(`/platform/businesses/${businessId}/products/${p.id}`, {
        [field]: !p[field],
      });
      await load();
    } catch {
      toast.error(lang === "ar" ? "فشل التحديث" : "Failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(lang === "ar" ? "حذف نهائي؟" : "Delete permanently?")) return;
    try {
      await apiClient.delete(`/platform/businesses/${businessId}/products/${p.id}`);
      await load();
    } catch {
      toast.error(lang === "ar" ? "فشل الحذف" : "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="dash-products">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] text-[#525252]">
          {lang === "ar" ? `الأنواع المتاحة: ` : `Available kinds: `}
          {allowedKinds.map((k) => (
            <span key={k} className="inline-block bg-[#faf9f5] rounded-full px-2 py-0.5 mx-1 text-[11px] font-semibold text-[#8a6f2e]">
              {lang === "ar" ? KIND_LABELS[k]?.ar : KIND_LABELS[k]?.en}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={load}
          className="ms-auto inline-flex items-center gap-1.5 text-[12px] text-[#525252] hover:text-[#0a0a0a]"
          data-testid="products-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
        <button
          type="button"
          onClick={() => setEditing({ __new: true, kind: allowedKinds[0], is_active: true, is_public: true, spec: {} })}
          className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-bold shadow-sm"
          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
          data-testid="products-add"
        >
          <Plus className="w-4 h-4" /> {lang === "ar" ? "إضافة" : "Add"}
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#8a8a8a]">…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 text-[#8a8a8a]" data-testid="products-empty">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {lang === "ar" ? "لا توجد منتجات/خدمات بعد. أضِف أول عنصر." : "No products yet. Add your first one."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((p) => {
            const kl = KIND_LABELS[p.kind] || { ar: p.kind, en: p.kind };
            return (
              <div key={p.id} className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4" data-testid={`product-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[15px] font-bold text-[#0a0a0a] truncate">{p.name_ar}</div>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#faf9f5] text-[#8a6f2e]">
                        {lang === "ar" ? kl.ar : kl.en}
                      </span>
                    </div>
                    {p.description_ar && (
                      <div className="text-[12px] text-[#525252] mt-1 line-clamp-2">{p.description_ar}</div>
                    )}
                    {p.price_sar != null && (
                      <div className="text-[12px] text-[#0a0a0a] mt-1">
                        {lang === "ar" ? "السعر: " : "Price: "}<span className="font-bold">{Number(p.price_sar).toLocaleString()}</span> {lang === "ar" ? "ر.س" : "SAR"}
                      </div>
                    )}
                    {Object.keys(p.spec || {}).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(p.spec).slice(0, 4).map(([k, v]) => (
                          <span key={k} className="text-[10px] bg-[#faf9f5] rounded px-1.5 py-0.5">
                            <span className="text-[#8a6f2e] font-bold">{k}:</span> {String(v).slice(0, 40)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => toggle(p, "is_active")}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                      p.is_active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-neutral-100 text-neutral-600 border-neutral-200"
                    }`}
                    data-testid={`product-active-${p.id}`}
                  >
                    {p.is_active ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                    {p.is_active ? (lang === "ar" ? "مفعّل" : "Active") : (lang === "ar" ? "معطّل" : "Inactive")}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(p, "is_public")}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                      p.is_public
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-neutral-100 text-neutral-600 border-neutral-200"
                    }`}
                    data-testid={`product-public-${p.id}`}
                  >
                    {p.is_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {p.is_public ? (lang === "ar" ? "عام" : "Public") : (lang === "ar" ? "خاص" : "Private")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    className="ms-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#0a0a0a] text-[#f7eecd]"
                    data-testid={`product-edit-${p.id}`}
                  >
                    <Edit3 className="w-3 h-3" /> {lang === "ar" ? "تعديل" : "Edit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200"
                    data-testid={`product-delete-${p.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <ProductEditor
          businessId={businessId}
          allowedKinds={allowedKinds}
          existing={editing.__new ? null : editing}
          initialKind={editing.kind}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function ProductEditor({ businessId, allowedKinds, existing, initialKind, onClose, onSaved }) {
  const { lang } = useI18n();
  const [form, setForm] = useState(() => existing || {
    kind: initialKind || allowedKinds[0],
    name_ar: "",
    name_en: "",
    description_ar: "",
    price_sar: "",
    is_active: true,
    is_public: true,
    spec: {},
  });
  const [busy, setBusy] = useState(false);

  const fields = KIND_FIELDS[form.kind] || KIND_FIELDS.other;

  const setSpec = (k, v) => setForm((f) => ({ ...f, spec: { ...(f.spec || {}), [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name_ar.trim()) {
      toast.error(lang === "ar" ? "الاسم بالعربية مطلوب" : "Arabic name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        name_ar: form.name_ar.trim(),
        name_en: (form.name_en || "").trim() || null,
        description_ar: (form.description_ar || "").trim() || null,
        price_sar: form.price_sar === "" || form.price_sar == null ? null : Number(form.price_sar),
        spec: form.spec || {},
      };
      if (existing) {
        await apiClient.patch(`/platform/businesses/${businessId}/products/${existing.id}`, payload);
        toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      } else {
        await apiClient.post(`/platform/businesses/${businessId}/products`, payload);
        toast.success(lang === "ar" ? "تمت الإضافة" : "Added");
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "object" ? (lang === "ar" ? d.ar : d.en) : d) || (lang === "ar" ? "فشل" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100]" data-testid="product-editor-modal">
      <button type="button" aria-label="close" onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="absolute inset-x-4 top-8 bottom-8 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-16 sm:bottom-16 sm:w-[560px] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="p-5 border-b border-[#0a0a0a]/[0.06] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
              {lang === "ar" ? "منتج / خدمة" : "Product / Service"}
            </div>
            <h3 className="text-lg font-display font-bold text-[#0a0a0a] mt-1">
              {existing ? (lang === "ar" ? "تعديل عنصر" : "Edit item") : (lang === "ar" ? "إضافة جديد" : "Add new")}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a8a8a] hover:text-[#0a0a0a]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 flex-1 overflow-y-auto space-y-3">
          <label className="block">
            <div className="text-[11px] font-bold text-[#525252] mb-1">{lang === "ar" ? "النوع" : "Kind"}</div>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value, spec: {} })}
              className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
              data-testid="product-form-kind"
            >
              {allowedKinds.map((k) => (
                <option key={k} value={k}>{lang === "ar" ? KIND_LABELS[k]?.ar : KIND_LABELS[k]?.en}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] font-bold text-[#525252] mb-1">{lang === "ar" ? "الاسم بالعربي *" : "Arabic name *"}</div>
            <input required value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                   className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" data-testid="product-form-name-ar" />
          </label>
          <label className="block">
            <div className="text-[11px] font-bold text-[#525252] mb-1">{lang === "ar" ? "الاسم بالإنجليزي" : "English name"}</div>
            <input value={form.name_en || ""} onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                   dir="ltr" className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" data-testid="product-form-name-en" />
          </label>
          <label className="block">
            <div className="text-[11px] font-bold text-[#525252] mb-1">{lang === "ar" ? "الوصف" : "Description"}</div>
            <textarea rows={2} value={form.description_ar || ""} onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm resize-none" />
          </label>
          <label className="block">
            <div className="text-[11px] font-bold text-[#525252] mb-1">{lang === "ar" ? "السعر (اختياري ر.س)" : "Price (optional, SAR)"}</div>
            <input type="number" value={form.price_sar ?? ""} onChange={(e) => setForm({ ...form, price_sar: e.target.value })}
                   className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" dir="ltr" data-testid="product-form-price" />
          </label>

          <div className="rounded-xl bg-[#faf9f5] border border-[#0a0a0a]/[0.05] p-3">
            <div className="text-[11px] font-bold text-[#8a6f2e] uppercase tracking-widest mb-2">
              {lang === "ar" ? "حقول خاصة بالنوع" : "Kind-specific fields"}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {fields.map((f) => (
                <label key={f.key} className="block">
                  <div className="text-[10px] font-semibold text-[#525252] mb-0.5">{lang === "ar" ? f.ar : f.en}</div>
                  <input
                    type={f.type === "number" ? "number" : "text"}
                    value={form.spec?.[f.key] ?? ""}
                    onChange={(e) => setSpec(f.key, f.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
                    dir={f.type === "number" ? "ltr" : "auto"}
                    data-testid={`product-spec-${f.key}`}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-1">
            <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#0a0a0a]">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="product-form-active" />
              {lang === "ar" ? "مفعّل" : "Active"}
            </label>
            <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#0a0a0a]">
              <input type="checkbox" checked={!!form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} data-testid="product-form-public" />
              {lang === "ar" ? "ظاهر في الملف العام" : "Show on public profile"}
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#0a0a0a]/[0.06]">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#525252]">
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </button>
            <button type="submit" disabled={busy}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold shadow-sm disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    data-testid="product-form-submit">
              <Check className="w-4 h-4" /> {lang === "ar" ? "حفظ" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
